import { useEffect, useRef, useState } from 'react';
import { Check, CreditCard, ExternalLink, Plus, ShieldCheck, Trash2 } from 'lucide-react';
import {
  confirmPaymentMethodSetup,
  createPaymentMethodSetup,
  getBillingPortal,
  getPaymentMethods,
  removePaymentMethod,
  setDefaultPaymentMethod,
} from '../lib/api';
import './PaymentMethodsPanel.css';

function cardBrandLabel(brand) {
  const labels = {
    amex: 'American Express',
    diners: 'Diners Club',
    mastercard: 'Mastercard',
    unionpay: 'UnionPay',
    visa: 'Visa',
  };
  return labels[String(brand || '').toLowerCase()] || 'Card';
}

function formatExpiry(month, year) {
  return `${String(month || 0).padStart(2, '0')}/${String(year || '').slice(-2)}`;
}

function isExpired(method) {
  if (!method?.expYear || !method?.expMonth) return false;
  const now = new Date();
  const expiry = new Date(method.expYear, method.expMonth, 1);
  return expiry <= new Date(now.getFullYear(), now.getMonth(), 1);
}

function cardBrandClass(brand) {
  const normalizedBrand = String(brand || '').toLowerCase();
  return ['amex', 'mastercard', 'visa'].includes(normalizedBrand) ? normalizedBrand : 'generic';
}

function PhysicalPaymentCard({
  method,
  index,
  expired,
  cardholderFallback = 'Organization card',
}) {
  const cardRef = useRef(null);
  const brand = cardBrandClass(method.brand);

  const resetCardPosition = () => {
    const card = cardRef.current;
    if (!card) return;
    card.style.removeProperty('--pm-card-rotate-x');
    card.style.removeProperty('--pm-card-rotate-y');
    card.style.removeProperty('--pm-card-glare-x');
    card.style.removeProperty('--pm-card-glare-y');
  };

  const handlePointerMove = (event) => {
    if (event.pointerType === 'touch'
      || window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches) return;

    const card = cardRef.current;
    const bounds = card?.getBoundingClientRect();
    if (!card || !bounds?.width || !bounds?.height
      || !Number.isFinite(event.clientX) || !Number.isFinite(event.clientY)) return;

    const x = Math.min(Math.max((event.clientX - bounds.left) / bounds.width, 0), 1);
    const y = Math.min(Math.max((event.clientY - bounds.top) / bounds.height, 0), 1);
    card.style.setProperty('--pm-card-rotate-x', `${((0.5 - y) * 8).toFixed(2)}deg`);
    card.style.setProperty('--pm-card-rotate-y', `${((x - 0.5) * 10).toFixed(2)}deg`);
    card.style.setProperty('--pm-card-glare-x', `${(x * 100).toFixed(1)}%`);
    card.style.setProperty('--pm-card-glare-y', `${(y * 100).toFixed(1)}%`);
  };

  return (
    <div
      className="payment-card-stage"
      style={{ '--pm-card-delay': `${Math.min(index * 45, 180)}ms` }}
      aria-hidden="true"
    >
      <div
        ref={cardRef}
        className={`payment-card payment-card--${brand}${method.isDefault ? ' payment-card--default' : ''}${expired ? ' payment-card--expired' : ''}`}
        onPointerMove={handlePointerMove}
        onPointerLeave={resetCardPosition}
        onPointerCancel={resetCardPosition}
      >
        <div className="payment-card__texture" />
        <div className="payment-card__glare" />
        <div className="payment-card__topline">
          <div className="payment-card__hardware">
            <span className="payment-card__chip"><i /><i /><i /></span>
            <span className="payment-card__contactless"><i /><i /><i /></span>
          </div>
          <span className="payment-card__brand">
            <i className="payment-card__brand-symbol" />
            {cardBrandLabel(method.brand)}
          </span>
        </div>
        <div className="payment-card__number">••••&nbsp; ••••&nbsp; ••••&nbsp; {method.last4}</div>
        <div className="payment-card__footer">
          <span>
            <small>Cardholder</small>
            <strong>{method.cardholderName || cardholderFallback}</strong>
          </span>
          <span>
            <small>Expires</small>
            <strong>{formatExpiry(method.expMonth, method.expYear)}</strong>
          </span>
        </div>
      </div>
    </div>
  );
}

export default function PaymentMethodsPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [message, setMessage] = useState(null);
  const [actionId, setActionId] = useState(null);
  const [removeConfirmationId, setRemoveConfirmationId] = useState(null);
  const handledReturnRef = useRef(false);

  const loadPaymentMethods = async () => {
    const result = await getPaymentMethods();
    setData(result);
    return result;
  };

  useEffect(() => {
    let active = true;

    const initialize = async () => {
      setLoading(true);
      setError(null);
      try {
        const params = new URLSearchParams(window.location.search);
        const setupStatus = params.get('payment_method');
        const sessionId = params.get('session_id');

        if (!handledReturnRef.current && setupStatus) {
          handledReturnRef.current = true;
          if (setupStatus === 'success' && sessionId) {
            const result = await confirmPaymentMethodSetup(sessionId);
            if (active) setMessage({ type: 'success', text: result.message || 'Payment method added.' });
          } else if (setupStatus === 'cancelled' && active) {
            setMessage({ type: 'neutral', text: 'No changes were made.' });
          }

          const cleanUrl = new URL(window.location.href);
          cleanUrl.searchParams.delete('payment_method');
          cleanUrl.searchParams.delete('session_id');
          window.history.replaceState(
            {},
            '',
            `${cleanUrl.pathname}${cleanUrl.search}${cleanUrl.hash}`,
          );
        }

        const result = await loadPaymentMethods();
        if (active) setData(result);
      } catch (err) {
        if (active) setError('Unable to load payment methods.');
      } finally {
        if (active) setLoading(false);
      }
    };

    initialize();
    return () => { active = false; };
  }, []);

  useEffect(() => {
    const scrollToPanel = () => {
      if (window.location.hash !== '#payment-methods') return;
      window.requestAnimationFrame(() => {
        document.getElementById('payment-methods')?.scrollIntoView?.({
          behavior: 'smooth',
          block: 'start',
        });
      });
    };
    scrollToPanel();
    window.addEventListener('hashchange', scrollToPanel);
    return () => window.removeEventListener('hashchange', scrollToPanel);
  }, []);

  const runAction = async (id, action, successMessage) => {
    setActionId(id);
    setError(null);
    setMessage(null);
    try {
      await action();
      await loadPaymentMethods();
      setRemoveConfirmationId(null);
      setMessage({ type: 'success', text: successMessage });
    } catch (err) {
      setError('The billing update could not be completed.');
    } finally {
      setActionId(null);
    }
  };

  const handleAdd = async () => {
    setActionId('add');
    setError(null);
    setMessage(null);
    try {
      const returnUrl = `${window.location.origin}/account#payment-methods`;
      const result = await createPaymentMethodSetup(returnUrl);
      window.location.assign(result.checkoutUrl);
    } catch (err) {
      setError('Unable to open Stripe Checkout.');
      setActionId(null);
    }
  };

  const handlePortal = async () => {
    setActionId('portal');
    setError(null);
    try {
      const result = await getBillingPortal(`${window.location.origin}/account#payment-methods`);
      window.location.assign(result.url);
    } catch (err) {
      setError('Unable to open the Stripe billing portal.');
      setActionId(null);
    }
  };

  const ownerName = data?.billingOwner?.displayName || data?.billingOwner?.email || 'the organization owner';
  const methods = data?.paymentMethods || [];
  const personalMethods = data?.personalPaymentMethods || [];

  return (
    <section
      className={`account-section payment-methods${!loading && methods.length === 0 ? ' payment-methods--empty' : ''}`}
      id="payment-methods"
      aria-labelledby="payment-methods-title"
    >
      <div className="payment-methods__header">
        <div>
          <h2 className="account-section-title" id="payment-methods-title">Payment Methods</h2>
          {(loading || methods.length > 0) && (
            <p className="payment-methods__description">
              Cards used for organization subscriptions, usage charges, and automatic credit reloads.
            </p>
          )}
        </div>
        {data?.canManage && (
          <button
            type="button"
            className="payment-methods__button payment-methods__button--primary"
            onClick={handleAdd}
            disabled={Boolean(actionId)}
            aria-label="Add payment method"
          >
            <Plus size={15} aria-hidden="true" />
            {actionId === 'add' ? 'Opening…' : 'Add card'}
          </button>
        )}
      </div>

      {loading ? (
        <div className="payment-methods__skeleton" aria-label="Loading payment methods">
          <span />
          <span />
        </div>
      ) : (
        <>
          {!data?.canManage && (
            <div className="payment-methods__owner-notice">
              <ShieldCheck size={16} aria-hidden="true" />
              <div>
                <strong>Managed by {ownerName}</strong>
                <span>Only the organization owner can add, remove, or change the default card.</span>
              </div>
            </div>
          )}

          {error && (
            <div className="payment-methods__message payment-methods__message--error" role="alert">
              <span>{error}</span>
              <button type="button" onClick={() => { setError(null); setLoading(true); loadPaymentMethods().then(setData).catch((err) => { console.error('Failed to reload payment methods:', err); setError('Unable to load payment methods. Please try again.'); }).finally(() => setLoading(false)); }}>
                Try again
              </button>
            </div>
          )}
          {message && (
            <div className={`payment-methods__message payment-methods__message--${message.type}`} role="status">
              {message.type === 'success' && <Check size={15} aria-hidden="true" />}
              <span>{message.text}</span>
            </div>
          )}

          {methods.length === 0 ? (
            <div className="payment-methods__empty">
              <div className="payment-methods__empty-icon"><CreditCard size={20} aria-hidden="true" /></div>
              <div>
                <strong>{data?.organizationId ? 'No organization payment method' : 'No payment method on file'}</strong>
                <span>
                  {data?.canManage
                    ? 'Add a card through Stripe for subscriptions, usage billing, or auto-reload.'
                    : `Ask ${ownerName} to add a card for the organization.`}
                </span>
              </div>
            </div>
          ) : (
            <div className="payment-methods__list">
              {methods.map((method, index) => {
                const expired = isExpired(method);
                const settingDefault = actionId === `default:${method.id}`;
                const removing = actionId === `remove:${method.id}`;
                const confirmingRemoval = removeConfirmationId === method.id;
                return (
                  <article className={`payment-methods__row${method.isDefault ? ' payment-methods__row--default' : ''}`} key={method.id}>
                    <PhysicalPaymentCard method={method} index={index} expired={expired} />
                    <div className="payment-methods__card-copy">
                      <div className="payment-methods__card-title">
                        <strong>{cardBrandLabel(method.brand)} •••• {method.last4}</strong>
                        {method.isDefault && <span className="payment-methods__badge">Default</span>}
                        {expired && <span className="payment-methods__badge payment-methods__badge--danger">Expired</span>}
                      </div>
                      <span>
                        Expires {formatExpiry(method.expMonth, method.expYear)}
                        {method.cardholderName ? ` · ${method.cardholderName}` : ''}
                      </span>
                    </div>
                    {data?.canManage && (
                      <div className="payment-methods__actions">
                        {!method.isDefault && !confirmingRemoval && (
                          <button
                            type="button"
                            onClick={() => runAction(
                              `default:${method.id}`,
                              () => setDefaultPaymentMethod(method.id),
                              `${cardBrandLabel(method.brand)} ending in ${method.last4} is now the default.`,
                            )}
                            disabled={Boolean(actionId) || expired}
                          >
                            {settingDefault ? 'Updating…' : 'Make default'}
                          </button>
                        )}
                        {!method.isDefault && !confirmingRemoval && (
                          <button
                            type="button"
                            className="payment-methods__danger-action"
                            onClick={() => setRemoveConfirmationId(method.id)}
                            disabled={Boolean(actionId)}
                            aria-label={`Remove card ending in ${method.last4}`}
                          >
                            <Trash2 size={14} aria-hidden="true" /> Remove
                          </button>
                        )}
                        {confirmingRemoval && (
                          <div className="payment-methods__confirm">
                            <span>Remove this card?</span>
                            <button
                              type="button"
                              className="payment-methods__confirm-danger"
                              onClick={() => runAction(
                                `remove:${method.id}`,
                                () => removePaymentMethod(method.id),
                                'Payment method removed.',
                              )}
                              disabled={Boolean(actionId)}
                            >
                              {removing ? 'Removing…' : 'Remove'}
                            </button>
                            <button type="button" onClick={() => setRemoveConfirmationId(null)} disabled={Boolean(actionId)}>Cancel</button>
                          </div>
                        )}
                      </div>
                    )}
                  </article>
                );
              })}
            </div>
          )}

          {!data?.canManage && personalMethods.length > 0 && (
            <section className="payment-methods__personal" aria-labelledby="personal-payment-methods-title">
              <div className="payment-methods__personal-header">
                <div>
                  <h3 id="personal-payment-methods-title">Your saved cards</h3>
                  <p>
                    These cards belong to your account and are not used for {data?.organizationName || 'organization'} billing.
                  </p>
                </div>
                <span>Personal</span>
              </div>
              <div className="payment-methods__list">
                {personalMethods.map((method, index) => {
                  const expired = isExpired(method);
                  return (
                    <article className="payment-methods__row" key={`personal:${method.id}`}>
                      <PhysicalPaymentCard
                        method={method}
                        index={index}
                        expired={expired}
                        cardholderFallback="Personal card"
                      />
                      <div className="payment-methods__card-copy">
                        <div className="payment-methods__card-title">
                          <strong>{cardBrandLabel(method.brand)} •••• {method.last4}</strong>
                          {method.isDefault && <span className="payment-methods__badge">Default</span>}
                          {expired && <span className="payment-methods__badge payment-methods__badge--danger">Expired</span>}
                        </div>
                        <span>
                          Expires {formatExpiry(method.expMonth, method.expYear)}
                          {method.cardholderName ? ` · ${method.cardholderName}` : ''}
                        </span>
                      </div>
                    </article>
                  );
                })}
              </div>
            </section>
          )}

          <div className="payment-methods__footer">
            <span><ShieldCheck size={14} aria-hidden="true" /> Card details are stored and processed securely by Stripe.</span>
            {data?.canManage && data?.stripeCustomerConfigured && (
              <button type="button" onClick={handlePortal} disabled={Boolean(actionId)}>
                {actionId === 'portal' ? 'Opening…' : 'Billing portal'}
                <ExternalLink size={13} aria-hidden="true" />
              </button>
            )}
          </div>
        </>
      )}
    </section>
  );
}
